import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiShield, FiEye, FiDownload, FiTrash2, FiEdit, FiMail, FiCalendar, FiLock } = FiIcons;

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
              <h1 className="text-3xl font-bold text-gray-900">Personvernerklæring</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-blue-900 mb-3">Innledning</h2>
                <p className="text-blue-800 leading-relaxed">
                  Denne personvernerklæringen beskriver hvordan Employer Brand AS ("vi", "oss", "vår") 
                  som eier av varemerket HMSvarsling, samler inn, bruker og beskytter dine personopplysninger 
                  når du bruker vår plattform for varsling og HMS-rapportering.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiCalendar} className="text-blue-600 text-2xl mx-auto mb-2" />
                  <div className="text-sm text-gray-600">Sist oppdatert</div>
                  <div className="font-medium">25. mai 2024</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiLock} className="text-green-600 text-2xl mx-auto mb-2" />
                  <div className="text-sm text-gray-600">GDPR-kompatibel</div>
                  <div className="font-medium">Fullt etterlevelse</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiShield} className="text-purple-600 text-2xl mx-auto mb-2" />
                  <div className="text-sm text-gray-600">Sikkerhet</div>
                  <div className="font-medium">ISO 27001</div>
                </div>
              </div>
            </section>

            {/* Company Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Behandlingsansvarlig</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Selskapsinfo</h3>
                    <div className="space-y-1 text-sm text-gray-700">
                      <p><strong>Navn:</strong> Employer Brand AS</p>
                      <p><strong>Org.nr:</strong> 925 182 672 MVA</p>
                      <p><strong>Adresse:</strong> Motzfeldts gate 21, 0561 Oslo</p>
                      <p className="text-xs text-gray-500 mt-2">En del av Fugleberg Gruppen Holding AS</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Kontakt</h3>
                    <div className="space-y-1 text-sm text-gray-700">
                      <p><strong>E-post:</strong> personvern@hmsvarsling.no</p>
                      <p><strong>Telefon:</strong> 40 55 19 89</p>
                      <p><strong>Svartid:</strong> Innen 30 dager</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Collection */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Hvilke personopplysninger samler vi inn?</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 bg-blue-50 p-6 rounded-r-lg">
                  <h3 className="font-semibold text-blue-900 mb-3">1. Bedriftsinformasjon</h3>
                  <div className="text-blue-800 space-y-2">
                    <p><strong>Hva:</strong> Organisasjonsnummer, bedriftsnavn, bransje, antall ansatte</p>
                    <p><strong>Hvorfor:</strong> For å identifisere kunden og tilpasse tjenesten</p>
                    <p><strong>Grunnlag:</strong> Avtale (GDPR art. 6.1.b)</p>
                  </div>
                </div>

                <div className="border-l-4 border-green-500 bg-green-50 p-6 rounded-r-lg">
                  <h3 className="font-semibold text-green-900 mb-3">2. Kontaktpersoner</h3>
                  <div className="text-green-800 space-y-2">
                    <p><strong>Hva:</strong> Navn, e-postadresse, telefonnummer, rolle/stilling</p>
                    <p><strong>Hvorfor:</strong> For kommunikasjon og tilgangsstyring</p>
                    <p><strong>Grunnlag:</strong> Avtale (GDPR art. 6.1.b)</p>
                  </div>
                </div>

                <div className="border-l-4 border-orange-500 bg-orange-50 p-6 rounded-r-lg">
                  <h3 className="font-semibold text-orange-900 mb-3">3. Varslingsdata</h3>
                  <div className="text-orange-800 space-y-2">
                    <p><strong>Hva:</strong> Anonyme varsler, HMS-rapporter, handlingsplaner</p>
                    <p><strong>Hvorfor:</strong> For å levere varslingstjenesten og dokumentere oppfølging</p>
                    <p><strong>Grunnlag:</strong> Rettslig forpliktelse (GDPR art. 6.1.c - Arbeidsmiljøloven)</p>
                  </div>
                </div>

                <div className="border-l-4 border-purple-500 bg-purple-50 p-6 rounded-r-lg">
                  <h3 className="font-semibold text-purple-900 mb-3">4. Tekniske data</h3>
                  <div className="text-purple-800 space-y-2">
                    <p><strong>Hva:</strong> IP-adresse, nettleserinfo, bruksmønstre</p>
                    <p><strong>Hvorfor:</strong> For sikkerhet, feilsøking og forbedring av tjenesten</p>
                    <p><strong>Grunnlag:</strong> Berettiget interesse (GDPR art. 6.1.f)</p>
                  </div>
                </div>
              </div>
            </section>

            {/* GDPR Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Dine rettigheter</h2>
              <p className="text-gray-700 mb-6">
                I henhold til GDPR har du følgende rettigheter når det gjelder dine personopplysninger:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiEye} className="text-blue-600 text-2xl flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rett til innsyn (Art. 15)</h3>
                    <p className="text-sm text-gray-600">
                      Du kan be om å få vite hvilke personopplysninger vi behandler om deg, 
                      hvorfor vi behandler dem og hvem vi eventuelt deler dem med.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiEdit} className="text-green-600 text-2xl flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rett til retting (Art. 16)</h3>
                    <p className="text-sm text-gray-600">
                      Du kan be om at feilaktige eller ufullstendige personopplysninger 
                      om deg blir rettet eller supplert.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiTrash2} className="text-red-600 text-2xl flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rett til sletting (Art. 17)</h3>
                    <p className="text-sm text-gray-600">
                      Du kan be om at personopplysninger om deg blir slettet. 
                      Dette gjelder ikke hvis vi har rettslig plikt til å beholde dem.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                  <SafeIcon icon={FiDownload} className="text-purple-600 text-2xl flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rett til dataportabilitet (Art. 20)</h3>
                    <p className="text-sm text-gray-600">
                      Du kan be om å få dine personopplysninger utlevert i et 
                      strukturert, maskinlesbart format.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-6">
                <h3 className="font-semibold text-yellow-900 mb-2">Slik utøver du dine rettigheter</h3>
                <div className="text-yellow-800 space-y-2">
                  <p>1. Send en e-post til <strong>personvern@hmsvarsling.no</strong></p>
                  <p>2. Oppgi hvilken rettighet du ønsker å utøve</p>
                  <p>3. Inkluder informasjon som kan hjelpe oss å identifisere deg</p>
                  <p>4. Vi svarer innen 30 dager</p>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-blue-900 mb-4">Spørsmål om personvern?</h2>
              <div className="flex items-start gap-4">
                <SafeIcon icon={FiMail} className="text-blue-600 text-2xl flex-shrink-0 mt-1" />
                <div>
                  <p className="text-blue-800 mb-3">
                    Har du spørsmål om hvordan vi behandler dine personopplysninger, 
                    eller ønsker å utøve dine rettigheter?
                  </p>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p><strong>E-post:</strong> personvern@hmsvarsling.no</p>
                    <p><strong>Telefon:</strong> 40 55 19 89</p>
                    <p><strong>Adresse:</strong> Employer Brand AS, Motzfeldts gate 21, 0561 Oslo</p>
                  </div>
                  <p className="text-xs text-blue-600 mt-3">
                    Du har også rett til å klage til Datatilsynet hvis du mener vi ikke 
                    behandler dine personopplysninger i henhold til loven.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;